<template>
  <div class="px-3 pb-2">
    <h4>{{ count.toLocaleString() }} éléments</h4>
    <v-form
      v-if="geocoderUrl && !config.hideSearchBar"
      ref="geocodeForm"
      v-model="geocodeFormValid"
      class="geocode-form"
      @submit.prevent="geocode(q)"
    >
      <v-text-field
        v-model="q"
        :rules="[() => !qInvalid || '']"
        placeholder="Saisissez une adresse"
        single-line
        append-icon="mdi-magnify"
        @click:append="geocode(q)"
      />
    </v-form>
    <v-row v-if="dataset">
      <v-col
        v-for="(filter, i) in config.dynamicFilters"
        :key="i"
        :cols="12"
        :sm="6"
        :md="12"
        class="py-0"
      >
        <v-select
          v-if="enums && filter.facet"
          v-model="filters[filter.field.key]"
          :dense="windowWidth <960"
          :label="label(filter.field.key)"
          :items="enums[filter.field.key]"
          multiple
          clearable
          @input="refresh()"
        />
        <v-autocomplete
          v-else
          v-model="filters[filter.field.key]"
          :dense="windowWidth <960"
          :loading="filterLoading"
          :label="label(filter.field.key)"
          :items="values[filter.field.key] || []"
          :filter="() => true"
          placeholder="Saisissez une valeur"
          hide-no-data
          multiple
          clearable
          @update:search-input="getValues(filter.field.key, $event)"
          @input="refresh()"
        >
          <template v-slot:item="data">
            <v-list-item-content v-html="filteredText(data.item, data.parent.searchInput)" />
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import axios from 'axios'
  import eventBus from '../../assets/event-bus.js'
  import { filters2qs } from '@data-fair/components-app/filters/filters'

  const zoomLevel = {
    housenumber: 17,
    street: 16,
    municipality: 12,
  }

  export default {
    props: {
      dense: { type: Boolean, default: false },
    },
    data: () => ({
      q: '',
      qInvalid: false,
      geocodeFormValid: true,
      filters: {},
      values: {},
      enums: null,
      filterLoading: false,
      count: 0,
    }),
    computed: {
      ...mapState(['dataset']),
      ...mapGetters(['geocoderUrl', 'config', 'datasetUrl']),
      fields () {
        return this.dataset.schema.reduce((a, b) => { a[b.key] = b; return a }, {})
      },
      query () {
        var ret = ''
        var qs = Object.keys(this.filters).filter(field => this.filters[field].length).map(field => `(${this.filters[field].map(f => `${field}:"${f}"`).join(' OR ')})`).join(' AND ')
        if (this.config.filters && this.config.filters.length) {
          qs += (qs.length ? ' AND ' : '') + filters2qs(this.config.filters)
        }
        if (qs.length) ret += '&qs=' + qs
        return ret
      },
    },
    watch: {
      dataset () {
        this.getEnums()
      },
    },
    async mounted () {
      this.filters = Object.assign({}, ...this.config.dynamicFilters.map(filter => ({ [filter.field.key]: [] })))
      this.refresh()
      if (this.dataset) await this.getEnums()
    },
    methods: {
      async geocode (q) {
        if (!q || !q.length) return
        if (this.gpsControl) this.gpsControl.updateActive(false)
        this.qInvalid = false
        try {
          const coord = (await axios.get(this.geocoderUrl + '/coord', { params: { q } })).data
          eventBus.$emit('goTo', [coord.lat, coord.lon], zoomLevel[coord.matchLevel] || 11)
        } catch (err) {
          console.log('Geocode error', err)
          this.qInvalid = true
        }
        this.$refs.geocodeForm.validate()
      },
      async getEnums () {
        if (!this.dataset || this.enums) return
        const facets = this.config.dynamicFilters.filter(filter => filter.facet)
        this.enums = Object.assign({}, ...facets.map(filter => ({ [filter.field.key]: this.fields[filter.field.key].enum || [] }),
        ))
        const qs = this.config.filters && this.config.filters.length && filters2qs(this.config.filters)
        for (const filter of facets) {
          const params = { field: filter.field.key, agg_size: 1000 }
          if (filter.sort === 'Alphabétique') params.sort = filter.field.key
          if (qs) params.qs = qs
          const results = (await axios.get(this.datasetUrl + '/values_agg', { params })).data
          const labels = this.fields[filter.field.key]['x-labels']
          this.enums[filter.field.key] = results.aggs.map(a => ({ text: `${labels ? labels[a.value] : a.value} (${a.total})`, value: a.value }))
          if (filter.sort === 'Alphabétique') this.enums[filter.field.key].sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()))
        }
      },
      async refresh (init) {
        eventBus.$emit('query', this.query)
        this.count = (await axios.get(`${this.datasetUrl}/lines?size=0${this.query}`)).data.total
      },
      label (key) {
        const field = this.dataset.schema.find(f => f.key === key)
        return field.title || field['x-originalName'] || field.key
      },
      async getValues (field, q) {
        this.filterLoading = true
        if (!q || q.length > 2) this.$set(this.values, field, (await axios.get(this.datasetUrl + '/values/' + field, { params: { q, q_mode: 'complete' } })).data)
        this.filterLoading = false
      },
      filteredText (item, search) {
        if (!search) return item
        const nItem = item.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        const nSearch = search.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        const idx = nItem.indexOf(nSearch)
        if (idx < 0) return item
        const start = item.slice(0, idx)
        const middle = item.slice(idx, idx + search.length)
        const end = item.slice(idx + search.length)
        return `<div>${start}<span class="v-list-item__mask">${middle}</span>${end}</div>`
      },
    },
  }
</script>
